import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  DataTable,
  Input,
  PageHead,
  Panel,
  SelectX,
  StatCard,
  StatusPill,
  type Column,
} from "@guestpilot/ui-kit";
import { DocumentText20Regular, Money20Regular, Warning20Regular } from "@fluentui/react-icons";
import { useGet, useGetPages, type Schema } from "../api";
import { formatDate, formatMoney, formatNumber } from "../format";

type Invoice = Schema<"Invoice">;
type Stats = Schema<"BillingStats">;

const STATUSES = [
  { value: "", label: "All statuses" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
];
const LABEL: Record<string, string> = {
  paid: "Paid",
  pending: "Pending",
  failed: "Failed",
  refunded: "Refunded",
};

/** The invoice ledger: a read-only mirror of the payment provider (BR-255, BR-256). PDFs are the provider's, never rendered here. */
export function Billing() {
  const [params, setParams] = useSearchParams();
  const get = (k: string) => params.get(k) ?? "";
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const stats = useGet<Stats>("/billing/stats");
  const query = {
    ...(get("q") ? { q: get("q") } : {}),
    ...(get("status") ? { status: get("status") } : {}),
    ...(get("from") ? { from: get("from") } : {}),
    ...(get("to") ? { to: get("to") } : {}),
    ...(get("organizationId") ? { organizationId: get("organizationId") } : {}),
  };
  const list = useGetPages<Invoice>("/billing/invoices", query);
  const columns: Column<Invoice>[] = [
    { key: "n", header: "Invoice", render: (i) => <span className="t-mono">{i.number}</span> },
    {
      key: "c",
      header: "Customer",
      render: (i) => (
        <Link to={`/customers/${i.organizationId}`} className="t-body-strong">
          {i.customerName}
        </Link>
      ),
    },
    { key: "a", header: "Amount", numeric: true, render: (i) => formatMoney(i.amountCents, i.currency) },
    {
      key: "s",
      header: "Status",
      render: (i) => (
        <StatusPill
          domain="invoice"
          value={i.status ?? "pending"}
          label={LABEL[i.status ?? "pending"] ?? ""}
        />
      ),
    },
    { key: "d", header: "Date", render: (i) => formatDate(i.issuedAt) },
    {
      key: "p",
      header: "Invoice PDF",
      render: (i) =>
        i.hostedPdfUrl ? (
          <a
            href={i.hostedPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Download invoice ${i.number}`}
          >
            Download
          </a>
        ) : (
          "—"
        ),
    },
  ];
  return (
    <div className="gp-page">
      <PageHead title="Billing" subtitle="Invoices across every customer." />
      <div className="gp-grid-3">
        <StatCard icon={<Money20Regular />} label="MRR" value={formatMoney(stats.data?.mrrCents ?? 0)} />
        <StatCard
          icon={<Warning20Regular />}
          label="Failed payments"
          value={formatMoney(stats.data?.failedAmountCents ?? 0)}
          caption={`${formatNumber(stats.data?.failedCount ?? 0)} invoices`}
          attention={(stats.data?.failedCount ?? 0) > 0}
        />
        <StatCard
          icon={<DocumentText20Regular />}
          label="Invoices this month"
          value={formatNumber(stats.data?.invoicesThisMonth ?? 0)}
        />
      </div>
      <div className="gp-row">
        <Input
          type="search"
          aria-label="Search by invoice number or customer"
          placeholder="Invoice number or customer"
          value={get("q")}
          onChange={(e) => set("q", e.target.value)}
        />
        <SelectX
          aria-label="Status"
          value={get("status")}
          options={STATUSES}
          onChange={(e) => set("status", e.target.value)}
        />
        <Input
          type="date"
          aria-label="From"
          value={get("from")}
          onChange={(e) => set("from", e.target.value)}
        />
        <Input type="date" aria-label="To" value={get("to")} onChange={(e) => set("to", e.target.value)} />
        {get("organizationId") ? (
          <Button variant="ghost" onClick={() => set("organizationId", "")}>
            Clear customer filter
          </Button>
        ) : null}
      </div>
      <Panel label="Invoices">
        <DataTable
          caption="Invoices"
          columns={columns}
          rows={list.items}
          loading={list.isLoading}
          error={list.isError}
          onRetry={() => void list.refetch()}
          errorTitle="Could not load the invoices."
          retryLabel="Retry"
          rowKey={(i) => i.id ?? ""}
          emptyTitle="No invoices match these filters"
        />
        {list.hasNextPage ? (
          <div className="gp-actions">
            <Button onClick={() => void list.fetchNextPage()}>Load more</Button>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
